import { createSlice } from "@reduxjs/toolkit";

// Concierge support messenger state (SEC-05). Panel open/view/active thread, the
// unread pipeline and the send outbox live here (not component state) so they
// survive the AuthGuard full-tree remount on login/logout.
export const support = createSlice({
  name: "support",
  initialState: {
    open: false,
    view: "home", // home | list | conversation | new | kb | rg
    activeRef: null,
    config: null,
    // unread pipeline: mirrors the WS UnreadUpdated / poll object shape
    unread: { total: 0, by_ref: {} },
    // idle | connecting | live | degraded
    transport: "idle",
    // Whether the backend is serving the messenger to this player. null means
    // /initial-settings has not answered yet, which is NOT the same as false:
    // the account page has to wait rather than bounce a valid deep link.
    serving: null,
    // queued sends, newest last: { tempId, ref, body, client_ref, at, status, attempts }
    outbox: [],
  },
  reducers: {
    openPanel: (state, action) => {
      state.open = true;
      if (action.payload && action.payload.view) state.view = action.payload.view;
      if (action.payload && action.payload.ref !== undefined) state.activeRef = action.payload.ref;
    },
    closePanel: (state) => {
      state.open = false;
    },
    togglePanel: (state) => {
      state.open = !state.open;
    },
    setView: (state, action) => {
      state.view = action.payload;
    },
    setActiveRef: (state, action) => {
      state.activeRef = action.payload;
    },
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    setUnread: (state, action) => {
      const total = Number(action.payload && action.payload.total) || 0;
      state.unread = { total, by_ref: (action.payload && action.payload.by_ref) || {} };
    },
    setTransport: (state, action) => {
      state.transport = action.payload;
    },
    setServing: (state, action) => {
      state.serving = action.payload === null ? null : !!action.payload;
    },
    hydrateOutbox: (state, action) => {
      state.outbox = Array.isArray(action.payload) ? action.payload : [];
    },
    enqueueOutbox: (state, action) => {
      state.outbox.push({ ...action.payload, status: "pending", attempts: 0 });
    },
    markOutboxSending: (state, action) => {
      const item = state.outbox.find((m) => m.tempId === action.payload);
      if (item) {
        item.status = "sending";
        item.attempts += 1;
      }
    },
    // The server row replaces the optimistic one, so the item leaves the queue
    // rather than being marked done: the thread reads outbox plus server
    // messages, and a sent-but-kept item would render twice.
    markOutboxSent: (state, action) => {
      state.outbox = state.outbox.filter((m) => m.tempId !== action.payload);
    },
    markOutboxFailed: (state, action) => {
      const item = state.outbox.find((m) => m.tempId === action.payload.tempId);
      if (item) {
        item.status = "failed";
        item.error = action.payload.status || null;
      }
    },
    retryOutbox: (state, action) => {
      const item = state.outbox.find((m) => m.tempId === action.payload);
      if (item) {
        item.status = "pending";
        item.error = null;
      }
    },
    discardOutbox: (state, action) => {
      state.outbox = state.outbox.filter((m) => m.tempId !== action.payload);
    },
  },
});

export const {
  openPanel,
  closePanel,
  togglePanel,
  setView,
  setActiveRef,
  setConfig,
  setUnread,
  setTransport,
  setServing,
  hydrateOutbox,
  enqueueOutbox,
  markOutboxSending,
  markOutboxSent,
  markOutboxFailed,
  retryOutbox,
  discardOutbox,
} = support.actions;

export default support.reducer;
